import {OrderProcessStateSyncMap} from '@web/core/constants'

import {POLLING_INTERVAL_TIME, SOCKET_WAIT_DATA_TIME, SOCKET_WAIT_TIME} from './constants'
import {disconnectSocket, registerSocket} from './createSocketIo'

type TimeoutType = ReturnType<typeof setTimeout>
type ApiError = Error | unknown

type PollingType =
    | 'disconnect'
    | 'connectError'
    | 'error'
    | 'connectTimeout'
    | 'socketTimeout'
    | 'socketTimeoutData'
    | 'socketError'
    | 'polling'

const PollingMap: Record<
    PollingType,
    {
        type: PollingType
        message: string
    }
> = {
    disconnect: {type: 'disconnect', message: 'disconnect'},
    connectError: {type: 'connectError', message: 'connect_error'},
    error: {type: 'error', message: 'error'},
    connectTimeout: {type: 'connectTimeout', message: 'connect_timeout'},
    socketTimeout: {type: 'socketTimeout', message: '3s exceed for socket'},
    socketTimeoutData: {type: 'socketTimeoutData', message: '15s exceed without response in socket'},
    socketError: {type: 'socketError', message: 'error in socket server'},
    polling: {type: 'polling', message: 'polling'},
}

export * from './createSocketIo'

export * from './commonSocket'

const delayTimeout = (timeout: number) =>
    new Promise((resolve) => {
        setTimeout(() => {
            resolve(null)
        }, timeout)
    })

function createQueue() {
    let list: [string, string][] = []
    const push = (message: string) => {
        list.push([message, new Date().toISOString()])
    }
    const toString = () => list.map(([message, date]) => `${message}${date}`).join(',')
    const clear = () => {
        list = []
    }
    return {
        push,
        clear,
        toString,
        popMessage: () => {
            const result = toString()
            clear()
            return result
        },
    }
}

/**
 * 결과를 전달해주는 소켓 프로세스
 * - 일정 시간 이후, 에러시 폴링으로 전환
 * - 결과 전달후 disconnected
 **/
export function createSocketProcessor<T extends object>({
    url,
    channel,
    type = 'socket',
    socketEvents = OrderProcessStateSyncMap,
    socketWaitTime = SOCKET_WAIT_TIME,
    socketWaitDataTime = SOCKET_WAIT_DATA_TIME,
    noticeReadyPromise,
    pollingPromise,
    pollingIntervalTime = POLLING_INTERVAL_TIME,
    endPollingWait,
    sendLogger,
}: {
    url: Maybe<string>
    channel: Maybe<string>
    /**
     * 소켓 프로세서 타입
     * - socket : session io 연결후, 폴링 전환
     * - polling : 폴링으로 바로 연결
     **/
    type?: Maybe<'socket' | 'polling'>
    /** 소켓에서 받을 수 있는 이벤트 목록 */
    socketEvents: string[]
    /** 소켓이 연결되기를 기다리는 시간 이후 폴링으로 전환 */
    socketWaitTime?: number
    /** 소켓에 응답이 있을때까지 기다리는 시간 이후 폴링으로 전환 */
    socketWaitDataTime?: number
    /** 소켓이나 폴링의 준비가 되었을때, 전달자에게 알려 주기 위한 action */
    noticeReadyPromise: () => Promise<void>
    /** 폴링의 간격 */
    pollingIntervalTime?: number[]
    /** 폴링으로 값을 가져오는 action */
    pollingPromise: () => Promise<T>
    /** 폴링을 끝내도 되는 값인지 확인 */
    endPollingWait: (data: T) => boolean
    /** 소켓의 연결과정 로깅을위한 action */
    sendLogger?: (message: string) => void
}) {
    let isReadyNotice = false
    let startedReadyNoticePolling = false
    let startedReadyNoticeSocket = false
    let startedPolling = false
    let finishedPolling = false
    let isDone = false
    const queue = createQueue()

    queue.push('create socket')
    const socketIo = registerSocket(url)

    let socketWaitTimer: Maybe<TimeoutType> = null
    let resultWaitTimer: Maybe<TimeoutType> = null
    function stopSocketWaitTimer() {
        if (socketWaitTimer) {
            clearTimeout(socketWaitTimer)
        }
    }
    function stopResultWaitTimer() {
        stopSocketWaitTimer()
        if (resultWaitTimer) {
            clearTimeout(resultWaitTimer)
        }
    }
    return new Promise<T>((resolve, reject) => {
        function handleDone() {
            isDone = true
            stopResultWaitTimer()
            disconnectSocket(url)
        }

        function handleFinish(payload: T) {
            handleDone()
            resolve(payload)
        }

        function handleAbort(e: ApiError) {
            handleDone()
            reject(e)
        }

        async function SendReadyNotice() {
            if (!isReadyNotice) {
                isReadyNotice = true
                try {
                    await noticeReadyPromise()
                } catch (e) {
                    handleAbort(e)
                    return false
                }
            }
            return true
        }

        async function StartPolling(pollingType: PollingType) {
            stopResultWaitTimer()
            queue.push(`${PollingMap[pollingType]?.message}`)
            if (startedPolling || isDone || startedReadyNoticePolling) {
                return
            }
            startedReadyNoticePolling = true
            // 서버에 준비완료됨을 전달했는지 여부
            if (!(await SendReadyNotice())) {
                queue.push(`notReady`)
                sendLogger?.(queue.toString())
                return
            }
            queue.push(`startPolling`)
            const message = queue.popMessage()
            if (message) {
                sendLogger?.(message)
            }

            startedPolling = true
            finishedPolling = false
            // 서버에 전달하였으나 응답에 오류가 있다면, polling 거부
            const last = pollingIntervalTime.length - 1
            // order 를 요청하자 마자, 상태를 조회하면, 불필요한 호출이 일어나기 때문에, 딜레이 선 반영
            for (let count = startedReadyNoticeSocket ? -1 : 0; !finishedPolling; count++) {
                if (count > -1) {
                    const index = count <= last ? count : last
                    await delayTimeout(pollingIntervalTime[index] ?? 0)
                }
                try {
                    const result: T = await pollingPromise()
                    if (!finishedPolling && endPollingWait(result)) {
                        finishedPolling = true
                        handleFinish(result)
                        return
                    }
                } catch (e: ApiError) {
                    finishedPolling = true
                    handleAbort(e)
                    return
                }
            }
        }

        if (type === 'polling') {
            // 소켓 서버에 문제가 있다면 바로 폴링 시작
            StartPolling(PollingMap.polling.type).then()
        } else if (socketIo) {
            const pollingWithLogger = (pollingType: PollingType) => {
                return () => {
                    StartPolling(pollingType).then()
                }
            }
            // 한번 연결이 끊어지면 다시 연결이 불가능하기 때문에 폴링으로 전환되도록
            socketIo.on('disconnect', pollingWithLogger(PollingMap.disconnect.type))
            socketIo.on('connect_error', pollingWithLogger(PollingMap.connectError.type))
            socketIo.on('error', pollingWithLogger(PollingMap.error.type))
            socketIo.on('connect_timeout', pollingWithLogger(PollingMap.connectTimeout.type))
            socketIo.on('connect', () => {
                queue.push('connected')
                socketIo.emit(
                    'nchat:channel',
                    {
                        command: 'subscribe',
                        name: {
                            channels: [channel],
                        },
                    },
                    () => {
                        queue.push('subscribe')
                        startedReadyNoticeSocket = true
                        SendReadyNotice().then()
                        stopSocketWaitTimer()
                        // 3초 이후 polling 으로 넘어감
                        resultWaitTimer = setTimeout(
                            pollingWithLogger(PollingMap.socketTimeoutData.type),
                            socketWaitDataTime,
                        )
                    },
                )
            })

            socketEvents.forEach((event) => {
                socketIo.on(event, handleFinish)
            })

            queue.push('connecting')
            socketIo.connect()

            // 3초 이후 polling 으로 넘어감
            socketWaitTimer = setTimeout(pollingWithLogger(PollingMap.socketTimeout.type), socketWaitTime)
        } else {
            // 소켓 서버에 문제가 있다면 바로 폴링 시작
            StartPolling(PollingMap.socketError.type).then()
        }
    })
}
